import { RefObject, useCallback, useEffect, useState } from 'react';

// Threshold in pixels for detecting edge hovering
const EDGE_THRESHOLD = 20;

type StartResizeState = {
  startWidth: number; // Initial width of the element before resizing
  startMousePosition: number; // Mouse position when resizing starts
};

type OriginalWidthState = {
  number: number; // Numeric width value of the element
  css: string; // Original CSS width of the element
};

const useResizable = (ref: RefObject<HTMLElement>) => {
  const [originalWidthState, setOriginalWidthState] =
    useState<OriginalWidthState>({
      number: 0,
      css: '',
    });

  const [startResizeState, setStartResizeState] =
    useState<StartResizeState | null>(null);

  const [isHoveringEdge, setIsHoveringEdge] = useState(false);

  // Handles mouse movement to update the element's width if resizing
  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!ref.current) return;

      const leftEdge = ref.current.offsetLeft;
      const rightEdge = leftEdge + ref.current.offsetWidth;
      setIsHoveringEdge(Math.abs(rightEdge - e.clientX) < EDGE_THRESHOLD);

      document.body.style.cursor = isHoveringEdge ? 'col-resize' : '';

      // No resize state, not resizing
      if (!startResizeState) return;

      document.body.style.userSelect = 'none';

      const { startWidth, startMousePosition } = startResizeState;
      const mouseMovement = e.clientX - startMousePosition;
      const newWidth = startWidth + mouseMovement;

      // Prevent resizing below the original width
      if (newWidth >= originalWidthState.number) {
        ref.current.style.width = `${newWidth}px`;
      }
    },
    [ref, isHoveringEdge, startResizeState, originalWidthState],
  );

  // Starts the resizing process
  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!ref.current || !isHoveringEdge) return;

      setStartResizeState({
        startWidth: ref.current.clientWidth,
        startMousePosition: e.clientX,
      });
    },
    [ref, isHoveringEdge],
  );

  // Ends the resizing process
  const onMouseUp = useCallback(() => {
    if (startResizeState) setStartResizeState(null);
    document.body.style.userSelect = '';
  }, [startResizeState]);

  // Resets the original width when the window is resized
  const onResize = useCallback(() => {
    if (!ref.current) return;
    ref.current.style.width = originalWidthState.css;
    setOriginalWidthState({
      number: ref.current.clientWidth,
      css: ref.current.style.width,
    });
  }, [originalWidthState.css, ref]);

  // Initializes the original width on mount
  useEffect(() => {
    if (!ref.current) return;
    setOriginalWidthState({
      number: ref.current.clientWidth,
      css: ref.current.style.width,
    });
  }, [ref]);

  // Sets up and cleans up event listeners
  useEffect(() => {
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', onResize);
    };
  }, [onMouseDown, onMouseMove, onMouseUp, onResize]);

  return { isHoveringEdge };
};

export default useResizable;
